import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { LoadingSpinner, EmptyState } from 'components/common';
import StackedBar from './StackedBar';
import StatTile from './StatTile';
import ObjectiveLogicNote from './ObjectiveLogicNote';
import { pct, ratioTone, fmtCount, share, RATIO_TONES, SEGMENT_COLORS } from './objectiveFormat';

const ON_TIME_KEY = 'earlier_than_2_days';

// Same order as the bar, so the legend and the table read top-to-bottom the
// same way. "Not measurable" is amber, not red: it is missing data, not a miss.
const BUCKET_COLORS = {
  earlier_than_2_days:   SEGMENT_COLORS.met,
  one_day_late:          '#fbbf24',
  two_days_late:         '#fb923c',
  more_than_2_days_late: SEGMENT_COLORS.delayed,
};

const CELL = 'tw-px-4 tw-py-2.5 tw-border-t tw-border-slate-100';

/**
 * Obj.3 — "OE SUMMARY": how quickly a job handed over by Sales made it into the
 * portal. Measured on the earliest CO of each job:
 *   delay = date(created_at) − entry_date, SLA = 2 days.
 */
const OeSummaryTab = ({ active, objectiveData, objectiveLoading: loading, objectiveError: error, onTotalsChange }) => {
  const data = objectiveData?.oe_summary;

  useEffect(() => {
    if (!active || !data) return;
    onTotalsChange(`${fmtCount(data.total)} jobs`);
  }, [active, data, onTotalsChange]);

  const segments = useMemo(() => {
    if (!data) return [];
    const buckets = (data.buckets ?? []).map((bucket) => ({
      key:   bucket.key,
      label: bucket.label,
      value: bucket.count ?? 0,
      color: BUCKET_COLORS[bucket.key] ?? SEGMENT_COLORS.neutral,
    }));

    return data.not_measurable > 0
      ? [...buckets, { key: 'not_measurable', label: 'Not measurable', value: data.not_measurable, color: SEGMENT_COLORS.pending }]
      : buckets;
  }, [data]);

  if (loading && !data) return <LoadingSpinner />;
  if (error) return <EmptyState title="Unable to load OE summary" description={error} />;
  if (!data || data.total === 0) {
    return <EmptyState title="No jobs in this range" description="Widen the entry-date range or clear the filters." />;
  }

  const onTime = data.buckets?.find((bucket) => bucket.key === ON_TIME_KEY);
  const lateCount = (data.buckets ?? [])
    .filter((bucket) => bucket.key !== ON_TIME_KEY)
    .reduce((sum, bucket) => sum + (bucket.count ?? 0), 0);

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div className="tw-flex tw-flex-wrap tw-gap-3">
        <StatTile
          label="Received from Sales"
          value={fmtCount(data.total)}
          hint="jobs (P/J number)"
          accent={SEGMENT_COLORS.primary}
        />
        <StatTile
          label="Entered within 2 days"
          value={pct(onTime?.ratio, 1)}
          hint={`${fmtCount(onTime?.count)} of ${fmtCount(data.total)} jobs`}
          tone={ratioTone(onTime?.ratio)}
          accent={ratioTone(onTime?.ratio)}
          bar={onTime?.ratio ?? 0}
        />
        <StatTile
          label="Late"
          value={fmtCount(lateCount)}
          hint={`${pct(share(lateCount, data.total), 1)} of jobs in scope`}
          tone={lateCount > 0 ? RATIO_TONES.bad : RATIO_TONES.neutral}
          accent={lateCount > 0 ? SEGMENT_COLORS.delayed : '#cbd5e1'}
        />
        {data.not_measurable > 0 && (
          <StatTile
            label="Not measurable"
            value={fmtCount(data.not_measurable)}
            hint="missing entry or created date"
            tone={RATIO_TONES.warning}
            accent={SEGMENT_COLORS.unknown}
          />
        )}
      </div>

      <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-4 tw-shadow-sm">
        <h3 className="tw-mb-3 tw-text-[13px] tw-font-semibold tw-text-slate-700">Entry timeliness</h3>
        <StackedBar segments={segments} total={data.total} height={16} />
      </div>

      <div className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-shadow-sm">
        <div className="tw-border-b tw-border-slate-100 tw-bg-slate-50/60 tw-px-4 tw-py-2">
          <h3 className="tw-mb-0 tw-text-[13px] tw-font-semibold tw-text-slate-700">Exact figures</h3>
        </div>
        <table className="tw-w-full tw-text-xs">
          <tbody>
            <tr className="tw-font-semibold tw-text-slate-700">
              <td className={CELL}>Total projects received from SLS</td>
              <td className={`${CELL} tw-text-right tw-tabular-nums`}>{fmtCount(data.total)}</td>
              <td className={`${CELL} tw-text-slate-400`}>(Proj)</td>
              <td className={`${CELL} tw-w-24 tw-text-right tw-text-slate-400`}>%</td>
            </tr>
            <tr>
              <td className={CELL}>Entered into the system</td>
              <td className={`${CELL} tw-text-right tw-tabular-nums`}>{fmtCount(data.entered)}</td>
              <td className={`${CELL} tw-text-slate-400`}>(Proj)</td>
              <td className={`${CELL} tw-text-right tw-font-semibold tw-tabular-nums`}>{pct(data.entered_ratio)}</td>
            </tr>
            {data.buckets?.map((bucket) => (
              <tr key={bucket.key} className={bucket.key === ON_TIME_KEY ? 'tw-bg-emerald-50/50' : ''}>
                <td className={`${CELL} tw-pl-8 tw-text-slate-600`}>
                  <span
                    className="tw-mr-2 tw-inline-block tw-h-2 tw-w-2 tw-rounded-sm tw-align-middle"
                    style={{ backgroundColor: BUCKET_COLORS[bucket.key] ?? SEGMENT_COLORS.neutral }}
                  />
                  {bucket.label}
                </td>
                <td className={`${CELL} tw-text-right tw-tabular-nums`}>{fmtCount(bucket.count)}</td>
                <td className={`${CELL} tw-text-slate-400`}>(Proj)</td>
                <td className={`${CELL} tw-text-right tw-font-semibold tw-tabular-nums`}>{pct(bucket.ratio)}</td>
              </tr>
            ))}
            {data.not_measurable > 0 && (
              <tr className="tw-bg-amber-50/50">
                <td className={`${CELL} tw-pl-8 tw-text-slate-600`}>
                  <span className="tw-mr-2 tw-inline-block tw-h-2 tw-w-2 tw-rounded-sm tw-align-middle" style={{ backgroundColor: SEGMENT_COLORS.pending }} />
                  Not measurable (no entry or created date)
                </td>
                <td className={`${CELL} tw-text-right tw-tabular-nums`}>{fmtCount(data.not_measurable)}</td>
                <td className={`${CELL} tw-text-slate-400`}>(Proj)</td>
                <td className={CELL} />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ObjectiveLogicNote items={[
        'The selected Entry Date range and filters define the jobs received from Sales in scope.',
        'One job means one P/J number, even when the job contains multiple buildings or change orders.',
        'Timeliness is measured only on the earliest change order of each job: Created date minus Entry date.',
        'A job is counted as Entered within 2 days when the record was created on or before Entry date + 2 days. Later records are grouped into 1 day late, 2 days late, or More than 2 days late.',
        'Jobs missing either the earliest Entry date or Created date are shown as Not measurable and are excluded from the timing buckets.',
      ]} />
    </div>
  );
};

OeSummaryTab.propTypes = {
  active:           PropTypes.bool.isRequired,
  objectiveData:    PropTypes.object,
  objectiveLoading: PropTypes.bool,
  objectiveError:   PropTypes.string,
  onTotalsChange:   PropTypes.func.isRequired,
};

export default OeSummaryTab;
